import { useCallback, useEffect, useRef, useState } from 'react';
import { sanitizeBlocksForPersist } from '../../../DocEditor/utils/imageFiles';
import { SopAPI } from '../../../../utils/api';
import { syncSnippetBacklinksForDoc } from '../../../../utils/snippetBacklinks';
import { collectTicks, setChecksFromMap } from '../utils/tickTree';
import { AUTOSAVE_DELAY } from '../constants';

// The save flush walks + re-serializes the whole tree — run it in an idle
// slot so it can't land exactly when the user resumes typing (the timeout
// still guarantees it runs soon even under continuous input)
const whenIdle = (fn) =>
  typeof requestIdleCallback === 'function' ? requestIdleCallback(fn, { timeout: 2000 }) : fn();

/**
 * Debounced body persist. Checkboxes added this session seed master defaults,
 * then every tick is pinned back so mission overrides never leak onto the doc.
 */
export function useLibraryDocAutosave({ docId, masterTicksRef, lastSavedBodyRef, setBlocks }) {
  const [saveState, setSaveState] = useState('idle'); // idle | dirty | saving | saved | error
  const pendingBodyRef = useRef(null);
  const timerRef = useRef(null);

  const toMasterBody = useCallback((tree) => {
    const masterTicks = masterTicksRef.current;
    const currentTicks = collectTicks(tree);
    currentTicks.forEach((checked, id) => {
      if (!masterTicks.has(id)) masterTicks.set(id, checked);
    });
    return sanitizeBlocksForPersist(setChecksFromMap(tree, masterTicks));
  }, [masterTicksRef]);

  const flush = useCallback(async () => {
    clearTimeout(timerRef.current);
    const tree = pendingBodyRef.current;
    if (!tree) return;
    pendingBodyRef.current = null;

    const masterBody = toMasterBody(tree);
    if (JSON.stringify(masterBody) === lastSavedBodyRef.current) {
      // tick-only change; nothing to persist
      if (!pendingBodyRef.current) setSaveState('saved');
      return;
    }

    setSaveState('saving');
    try {
      await SopAPI.update(docId, { body: masterBody });
      lastSavedBodyRef.current = JSON.stringify(masterBody);
      syncSnippetBacklinksForDoc(docId, masterBody).catch(() => {});
      setSaveState(pendingBodyRef.current ? 'dirty' : 'saved');
    } catch (e) {
      pendingBodyRef.current = pendingBodyRef.current || tree; // keep for retry
      setSaveState('error');
    }
  }, [docId, toMasterBody, lastSavedBodyRef]);

  const handleBlocksChange = useCallback(
    (tree) => {
      setBlocks(tree);
      // Keystroke path stays cheap: stash the tree and debounce; all the
      // master-tick reconciliation happens in flush.
      pendingBodyRef.current = tree;
      setSaveState((s) => (s === 'dirty' ? s : 'dirty'));
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => whenIdle(flush), AUTOSAVE_DELAY);
    },
    [flush, setBlocks]
  );

  // Flush pending edits on unmount (covers closing the tab or the modal)
  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      if (pendingBodyRef.current) {
        const masterBody = toMasterBody(pendingBodyRef.current);
        pendingBodyRef.current = null;
        SopAPI.update(docId, { body: masterBody })
          .then(() => syncSnippetBacklinksForDoc(docId, masterBody))
          .catch(() => {});
      }
    };
  }, [docId, toMasterBody]);

  return { saveState, setSaveState, handleBlocksChange };
}
